import React, {useState, useRef, useEffect, ReactNode} from "react";
import styles from "./styles.module.scss";
import {images} from "src/assets/images/images";
import {useAppSelector, useAppDispatch} from "src/hooks/useRedux";
import {setCurrentState} from "src/redux/slices/appSlice/AppSlice";
import Separator from "src/components/separator";
import {UIText} from "src/components/uIKit";
import {useNavigate} from "react-router-dom";
import {STATE, TEXT_TAGS} from "src/utils/enums";

interface AccordionProps {
  title: string;
  children: ReactNode;
  index: number;
  refreshHeight?: boolean;
  isDone: boolean;
  isOpen?: boolean;
  route: string;
  accordionIcon: string;
  ariaLabel?: string;
  onToggle?: () => void;
}

const Accordion: React.FC<AccordionProps> = ({
  title,
  children,
  index,
  refreshHeight,
  isDone,
  isOpen = false,
  route,
  accordionIcon,
  ariaLabel,
  onToggle,
}) => {
  const [isActive, setIsActive] = useState(false);
  const { strings, currentState, hostUrl, firstSectionValid, secondSectionValid, thirdSectionValid } = useAppSelector((state) => state.app);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    setIsActive(currentState === index);
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 100);
  }, [currentState]);

  useEffect(() => {
    setIsActive(isOpen);
    if (isOpen) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    }
  }, [isOpen]);

  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!contentRef.current) return;
    
    const content = contentRef.current;
    const focusableElements = content.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]'
    );

    if (isActive) {
      setTimeout(() => {
        content.style.maxHeight = `${content.scrollHeight+50}px`;
        focusableElements.forEach(el => (el as HTMLElement).tabIndex = 0);
      }, 500);
    } else {
      content.style.maxHeight = "0";
      focusableElements.forEach(el => (el as HTMLElement).tabIndex = -1);
    }
  }, [isActive, refreshHeight]);

  const checkIfSectionIsClickable = (index: number) => {
    if (index === 0) {
      return true;
    } else if (index === 1) {
      return firstSectionValid;
    } else if (index === 2) {
      return secondSectionValid;
    } else if (index === 3) {
      return thirdSectionValid;
    }
    return false;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && (isDone || checkIfSectionIsClickable(index))) {
      e.preventDefault(); // Prevent page scroll on space
      if (onToggle) {
        onToggle();
      } else {
        dispatch(setCurrentState(index));
        setIsActive(!isActive);
        if (route.length > 0) {
          navigate("/" + hostUrl + "/" + route);
        }
      }
    }
  };

  return (
    <div className={styles.accordionItem}>
      <div
        className={styles.accordionTitleWrapper}
        onClick={() => {
          if (
            STATE.TAX_REFAUND === index ||
            isDone ||
            checkIfSectionIsClickable(index)
          ) {
            if (onToggle) {
              onToggle();
            } else {
              dispatch(setCurrentState(index));
              setIsActive(!isActive);
              if (route.length > 0) {
                navigate("/" + hostUrl + "/" + route);
              }
            }
          }
        }}
      >
        <div className={styles.flex1}>
          <img
            src={isDone ? images.success : accordionIcon}
            alt=""
            className={styles.icon}
          />

          <UIText
            tabIndex={0}
            ariaLabel={ariaLabel + (checkIfSectionIsClickable(index) ? "" : strings.acc_accordion_not_allowed)}
            text={title}
            tag={TEXT_TAGS.H2}
            size={20}
            weight={500}
            style={styles.accordionTitle}
          />
          {/* {isDone && ( */}
          <button
            tabIndex={isDone || checkIfSectionIsClickable(index) ? 0 : -1}
            aria-hidden={isDone}
            aria-label={isActive ? strings.acc_t_toggle_step_close : strings.acc_t_toggle_step_open}
            aria-expanded={isActive}
            onKeyDown={handleKeyDown}
            className={styles.button}
            >
            <img
              src={isActive ? images.up : images.down}
              className={styles.arrow}
              alt="toggle-arrow"
            />
          </button>
          {/* )} */}
        </div>
        {isActive && <Separator className={styles.separator} />}
      </div>

      <div
        aria-hidden={!isOpen}
        // aria-atomic="true"
        // aria-live={isActive ? "polite" : "off"}
        ref={contentRef}
        className={styles.accordionContent}
        style={{
          maxHeight: "0",
          transition: "max-height 0.3s ease-out"
        }}
      >
        <div className={styles.accordionText}>{children}</div>
      </div>
    </div>
  );
};

export default Accordion;
